import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	Button,
	Card,
	CardBody,
	Col,
	Container,
	FormGroup,
	Input,
	Label,
	Row,
	Table,
} from 'reactstrap';
import { Breadcrumbs } from '../../components/Common';
import Spinner from '../../components/Common/Spinner';
import useAuthUser from '../../common/hooks/authUser';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { TDealerPortalNavState } from '../../common/dealerPortalNavigation';
import { toastError, toastSuccess } from '../../helpers/toast-helper';
import { getFormattedCurrency } from '../../helpers/currency-helper';
import {
	getDealerPortalCustomer,
	getDealerPortalInventory,
	parseInventoryTables,
	searchDealerPortalCustomers,
	updateDealerPortalUnit,
} from '../../api/dealerPortal';
import type {
	TSyteLineCustomer,
	TDealerInventoryUnitRow,
	TDealerPortalUnit,
} from '../../api/dealerPortal/types';
import { buildDealerInventoryPdfBytes, downloadDealerInventoryPdf } from '../../helpers/dealer-inventory-pdf';
import {
	extractCustomerNumberPart,
	formatDealerInputDisplay,
	normalizeCustomerNumberForLookup,
} from '../../helpers/dealer-customer-input';

type LocalInventoryRow = TDealerInventoryUnitRow & { __rowId: string };

function applyApiUnitToInventoryRow(
	row: TDealerInventoryUnitRow,
	unit: TDealerPortalUnit | null | undefined,
	keys: { soldKey?: string; rentalKey?: string },
): TDealerInventoryUnitRow {
	if (!unit) return row;
	const next = { ...row };
	if (keys.soldKey && unit.soldDate != null && String(unit.soldDate) !== '') {
		next[keys.soldKey] = unit.soldDate;
	}
	if (keys.rentalKey && typeof unit.rental === 'boolean') {
		next[keys.rentalKey] = unit.rental;
	}
	return next;
}

type TableBlock = {
	key: string;
	rows: LocalInventoryRow[];
};

const ON_HAND_KEYS = new Set(['onhandunits', 'onhandunit', 'on_hand_units']);

function humanizePropertyKey(key: string): string {
	if (key === '__rowId') return '';
	return key
		.replace(/([A-Z])/g, ' $1')
		.replace(/_/g, ' ')
		.replace(/^./, (s) => s.toUpperCase())
		.trim();
}

function newRowId(): string {
	if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
		return crypto.randomUUID();
	}
	return `r-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function withoutRowId(row: LocalInventoryRow): Omit<LocalInventoryRow, '__rowId'> {
	const { __rowId, ...rest } = row;
	void __rowId;
	return rest;
}

function toRowsWithIds(rows: TDealerInventoryUnitRow[]): LocalInventoryRow[] {
	return rows.map((r) => ({ ...r, __rowId: newRowId() }));
}

function resolveFieldKey(row: LocalInventoryRow, candidates: string[]): string | undefined {
	const keys = Object.keys(row).filter((k) => k !== '__rowId');
	const map = new Map(keys.map((k) => [k.toLowerCase(), k]));
	for (const c of candidates) {
		const hit = map.get(c.toLowerCase());
		if (hit) return hit;
	}
	return undefined;
}

function collectColumns(rows: LocalInventoryRow[]): string[] {
	const set = new Set<string>();
	for (const row of rows) {
		for (const k of Object.keys(row)) {
			if (k !== '__rowId') set.add(k);
		}
	}
	const all = [...set];
	const priority = (k: string) => {
		const l = k.toLowerCase();
		if (l === 'solddate' || l === 'sold_date') return 1;
		if (l === 'rental' || l === 'isrental') return 1;
		return 0;
	};
	return all.sort((a, b) => {
		const pa = priority(a);
		const pb = priority(b);
		if (pa !== pb) return pa - pb;
		if (pa === 1) {
			const tail = (k: string) => {
				const l = k.toLowerCase();
				if (l.includes('sold')) return 0;
				if (l.includes('rental')) return 1;
				return 2;
			};
			const ta = tail(a);
			const tb = tail(b);
			if (ta !== tb) return ta - tb;
		}
		return humanizePropertyKey(a).localeCompare(humanizePropertyKey(b));
	});
}

function formatCellForDisplay(key: string, value: unknown, tableKey?: string): string {
	if (value == null) return '';
	if (typeof value === 'boolean') return value ? 'Yes' : 'No';
	if (typeof value === 'number') {
		const lk = key.toLowerCase();
		if (lk.includes('amount') || lk.includes('price') || lk.includes('cost')) {
			return getFormattedCurrency(value);
		}
		return value.toLocaleString();
	}
	const s = String(value);
	if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
		const datePart = s.slice(0, 10);
		if (tableKey && isRegisteredUnitsTable(tableKey)) {
			if (datePart === '0001-01-01' || datePart === '1900-01-01') {
				return '';
			}
		}
		return datePart;
	}
	if (tableKey && isRegisteredUnitsTable(tableKey)) {
		const iso = toInputDateValue(value);
		if (iso === '0001-01-01' || iso === '1900-01-01') {
			return '';
		}
	}
	return s;
}

function toInputDateValue(value: unknown): string {
	if (value == null || value === '') return '';
	const s = String(value);
	if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
	const d = new Date(s);
	if (Number.isNaN(d.getTime())) return '';
	return d.toISOString().slice(0, 10);
}

/** On-hand sold date: show empty for unset / SyteLine sentinel dates. */
function toOnHandSoldDateInputValue(value: unknown): string {
	const v = toInputDateValue(value);
	if (!v) return '';
	if (v === '0001-01-01' || v === '1900-01-01') return '';
	return v;
}

function localTodayYmd(): string {
	const d = new Date();
	const y = d.getFullYear();
	const m = String(d.getMonth() + 1).padStart(2, '0');
	const day = String(d.getDate()).padStart(2, '0');
	return `${y}-${m}-${day}`;
}

function isOnHandTable(tableKey: string): boolean {
	return ON_HAND_KEYS.has(tableKey.toLowerCase());
}

function isPendingRegistrationsTable(tableKey: string): boolean {
	return tableKey.toLowerCase() === 'unitspendingregistrations';
}

function isRegisteredUnitsTable(tableKey: string): boolean {
	const k = tableKey.toLowerCase();
	return k === 'registredunits' || k === 'registeredunits';
}

function isRegistrationDateColumn(col: string): boolean {
	const lc = col.toLowerCase();
	return lc === 'registrationdate' || lc === 'registration_date';
}

function isRentalColumn(col: string): boolean {
	const lc = col.toLowerCase();
	return lc === 'rental' || lc === 'isrental';
}

/** Columns hidden for specific inventory tables (UI + PDF). */
function excludeColumnForTable(tableKey: string, col: string): boolean {
	if (isOnHandTable(tableKey) && isRegistrationDateColumn(col)) {
		return true;
	}
	if (isPendingRegistrationsTable(tableKey)) {
		if (isRegistrationDateColumn(col) || isRentalColumn(col)) {
			return true;
		}
	}
	return false;
}

function collectDisplayColumns(tableKey: string, rows: LocalInventoryRow[]): string[] {
	return collectColumns(rows).filter((col) => !excludeColumnForTable(tableKey, col));
}

const DealerInventoryPage = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { user, isAdmin, isDataLoading } = useAuthUser();
	const [dealerInput, setDealerInput] = useState('');
	const [customer, setCustomer] = useState<TSyteLineCustomer | null>(null);
	const [tables, setTables] = useState<TableBlock[]>([]);
	const [isLookupLoading, setIsLookupLoading] = useState(false);
	const [isPdfLoading, setIsPdfLoading] = useState(false);
	const [savingRowIds, setSavingRowIds] = useState<Set<string>>(() => new Set());
	const [searchSuggestions, setSearchSuggestions] = useState<TSyteLineCustomer[]>([]);
	const [isSearchOpen, setIsSearchOpen] = useState(false);
	const [isSearchLoading, setIsSearchLoading] = useState(false);
	const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const searchSeqRef = useRef(0);
	const dealerSearchWrapRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		document.title = 'Dealer inventory';
	}, []);

	useEffect(() => {
		if (user && !isAdmin) {
			toastError('Access denied. Admin privileges required.');
			navigate('/');
		}
	}, [user, isAdmin, navigate]);

	const onHandKey = useMemo(() => tables.find((t) => isOnHandTable(t.key))?.key ?? null, [tables]);

	const updateOnHandRow = useCallback(
		(rowId: string, patch: Record<string, unknown>) => {
			if (!onHandKey) return;
			setTables((prev) =>
				prev.map((block) => {
					if (block.key !== onHandKey) return block;
					return {
						...block,
						rows: block.rows.map((row) =>
							row.__rowId === rowId ? { ...row, ...patch } : row,
						),
					};
				}),
			);
		},
		[onHandKey],
	);

	const persistOnHandUnit = useCallback(
		async (
			rowId: string,
			serial: string,
			soldInputRaw: unknown,
			rental: boolean,
			sourceRow: LocalInventoryRow,
			keys: { soldKey?: string; rentalKey?: string },
		): Promise<boolean> => {
			if (!onHandKey) return false;
			if (!customer?.customerNumber) {
				toastError('No customer loaded.');
				return false;
			}
			const cust = normalizeCustomerNumberForLookup(customer.customerNumber);
			const serialTrim = serial.trim();
			if (!serialTrim) {
				toastError('Serial number is required to save.');
				return false;
			}
			const soldStr =
				typeof soldInputRaw === 'string' && soldInputRaw.trim()
					? soldInputRaw.trim()
					: toInputDateValue(soldInputRaw);
			const hasSoldDate = Boolean(soldStr?.trim());

			const workingRow: LocalInventoryRow = {
				...sourceRow,
				...(keys.soldKey ? { [keys.soldKey]: soldStr } : {}),
				...(keys.rentalKey ? { [keys.rentalKey]: rental } : {}),
			};

			setSavingRowIds((prev) => new Set(prev).add(rowId));
			try {
				const result = await updateDealerPortalUnit({
					customerNumber: cust,
					serialNumber: serialTrim,
					...(hasSoldDate ? { soldDate: soldStr } : {}),
					rental,
				});
				if (result.errorMessage) {
					toastError(result.errorMessage);
					return false;
				}
				const merged = applyApiUnitToInventoryRow(workingRow, result.unit ?? undefined, keys) as LocalInventoryRow;

				if (hasSoldDate) {
					setTables((prev) => {
						const pendingKeyLower = 'unitspendingregistrations';
						let foundPending = false;
						const next = prev.map((block) => {
							if (block.key === onHandKey) {
								return {
									...block,
									rows: block.rows.filter((r) => r.__rowId !== rowId),
								};
							}
							if (block.key.toLowerCase() === pendingKeyLower) {
								foundPending = true;
								const rest = withoutRowId(merged);
								return {
									...block,
									rows: [...block.rows, { ...rest, __rowId: newRowId() }],
								};
							}
							return block;
						});
						if (!foundPending) {
							const rest = withoutRowId(merged);
							const onHandIdx = next.findIndex((b) => b.key === onHandKey);
							const insertAt = onHandIdx >= 0 ? onHandIdx + 1 : next.length;
							next.splice(insertAt, 0, {
								key: 'unitsPendingRegistrations',
								rows: [{ ...rest, __rowId: newRowId() }],
							});
						}
						return next;
					});
					await toastSuccess('Unit updated and moved to Units Pending Registrations.');
				} else {
					setTables((prev) =>
						prev.map((block) =>
							block.key === onHandKey
								? {
										...block,
										rows: block.rows.map((r) =>
											r.__rowId === rowId
												? { ...(merged as LocalInventoryRow), __rowId: r.__rowId }
												: r,
										),
									}
								: block,
						),
					);
					await toastSuccess('Unit updated.');
				}
				return true;
			} catch (e) {
				toastError(e instanceof Error ? e.message : 'Update failed.');
				return false;
			} finally {
				setSavingRowIds((prev) => {
					const n = new Set(prev);
					n.delete(rowId);
					return n;
				});
			}
		},
		[customer, onHandKey],
	);

	const runLookup = useCallback(async (customerNumberOverride?: string) => {
		const raw = (customerNumberOverride ?? dealerInput).trim();
		if (!raw) {
			toastError('Enter a dealer number.');
			return;
		}
		const lookupNum = normalizeCustomerNumberForLookup(raw);
		if (!lookupNum) {
			toastError('Enter a dealer number.');
			return;
		}
		setIsSearchOpen(false);
		setIsLookupLoading(true);
		setCustomer(null);
		setTables([]);
		try {
			const cust = await getDealerPortalCustomer(lookupNum);
			if (cust.errorMessage) {
				toastError(cust.errorMessage);
				setIsLookupLoading(false);
				return;
			}
			setCustomer(cust);

			const inv = await getDealerPortalInventory(lookupNum);
			const parsed = parseInventoryTables(inv);
			setTables(
				parsed.map((p) => ({
					key: p.key,
					rows: toRowsWithIds(p.rows),
				})),
			);
		} catch (e) {
			toastError(e instanceof Error ? e.message : 'Lookup failed.');
		} finally {
			setIsLookupLoading(false);
		}
	}, [dealerInput]);

	const runLookupRef = useRef(runLookup);
	runLookupRef.current = runLookup;

	const rawCustomerParam = searchParams.get('customerNumber');
	const normalizedUrlCustomer = useMemo(
		() => normalizeCustomerNumberForLookup(rawCustomerParam ?? ''),
		[rawCustomerParam],
	);

	useEffect(() => {
		if (!user || !isAdmin || !normalizedUrlCustomer) return;
		setDealerInput(formatDealerInputDisplay(normalizedUrlCustomer, ''));
		void runLookupRef.current(normalizedUrlCustomer);
	}, [user, isAdmin, normalizedUrlCustomer]);

	useEffect(() => {
		const q = extractCustomerNumberPart(dealerInput);
		if (q.length < 3) {
			setSearchSuggestions([]);
			setIsSearchOpen(false);
			setIsSearchLoading(false);
			if (searchDebounceRef.current) {
				clearTimeout(searchDebounceRef.current);
				searchDebounceRef.current = null;
			}
			return;
		}

		setIsSearchLoading(true);
		if (searchDebounceRef.current) {
			clearTimeout(searchDebounceRef.current);
		}
		searchDebounceRef.current = setTimeout(async () => {
			const seq = ++searchSeqRef.current;
			try {
				const results = await searchDealerPortalCustomers(q);
				if (seq !== searchSeqRef.current) return;
				setSearchSuggestions(Array.isArray(results) ? results : []);
				setIsSearchOpen(true);
			} catch {
				if (seq !== searchSeqRef.current) return;
				setSearchSuggestions([]);
			} finally {
				if (seq === searchSeqRef.current) {
					setIsSearchLoading(false);
				}
			}
		}, 300);

		return () => {
			if (searchDebounceRef.current) {
				clearTimeout(searchDebounceRef.current);
				searchDebounceRef.current = null;
			}
		};
	}, [dealerInput]);

	useEffect(() => {
		const onDocMouseDown = (e: MouseEvent) => {
			if (!dealerSearchWrapRef.current?.contains(e.target as Node)) {
				setIsSearchOpen(false);
			}
		};
		document.addEventListener('mousedown', onDocMouseDown);
		return () => document.removeEventListener('mousedown', onDocMouseDown);
	}, []);

	const goToOrders = useCallback(() => {
		if (!customer?.customerNumber) {
			toastError('Look up a dealer first.');
			return;
		}
		const num = normalizeCustomerNumberForLookup(customer.customerNumber);
		navigate(`/admin/orders?customerNumber=${encodeURIComponent(num)}`, {
			state: { fromDealerInventory: true } satisfies TDealerPortalNavState,
		});
	}, [customer, navigate]);

	const goToInvoices = useCallback(() => {
		if (!customer?.customerNumber) {
			toastError('Look up a dealer first.');
			return;
		}
		const num = normalizeCustomerNumberForLookup(customer.customerNumber);
		navigate(`/admin/invoices?customerNumber=${encodeURIComponent(num)}`, {
			state: { fromDealerInventory: true } satisfies TDealerPortalNavState,
		});
	}, [customer, navigate]);

	const handlePrintPdf = useCallback(async () => {
		if (!customer) {
			toastError('Look up a dealer before printing.');
			return;
		}
		setIsPdfLoading(true);
		try {
			const summaryLines = [
				{ label: 'Open orders', value: getFormattedCurrency(customer.openOrdersAmount) },
				{ label: 'Open invoices', value: getFormattedCurrency(customer.openInvoiceAmount) },
				{ label: 'Last year sales', value: getFormattedCurrency(customer.lastYearSales) },
				{ label: 'YTD sales', value: getFormattedCurrency(customer.ytdSales) },
			];

			const pdfTables = tables.map((block) => {
				const cols = collectDisplayColumns(block.key, block.rows);
				const headers = cols.map(humanizePropertyKey).filter(Boolean);
				const rows = block.rows.map((row) =>
					cols.map((c) => formatCellForDisplay(c, row[c], block.key)),
				);
				return {
					title: humanizePropertyKey(block.key),
					columns: headers,
					rows,
				};
			});

			const bytes = await buildDealerInventoryPdfBytes({
				customerNumber: customer.customerNumber ?? '',
				customerName: customer.customerName ?? '',
				summaryLines,
				tables: pdfTables,
			});
			const safe = (customer.customerNumber ?? 'dealer').replace(/[^\w.-]+/g, '_');
			downloadDealerInventoryPdf(`dealer-inventory-${safe}.pdf`, bytes);
		} catch (e) {
			toastError(e instanceof Error ? e.message : 'Could not build PDF.');
		} finally {
			setIsPdfLoading(false);
		}
	}, [customer, tables]);

	if (isDataLoading) {
		return <Spinner />;
	}

	if (user && !isAdmin) {
		return null;
	}

	return (
		<Container fluid>
			<Breadcrumbs title="Dealer inventory" />
			<Row className="mb-3">
				<Col md={6} lg={4}>
					<FormGroup>
						<Label for="dealerNumber">Dealer number</Label>
						<div className="d-flex gap-2 align-items-start">
							<div className="position-relative flex-grow-1" ref={dealerSearchWrapRef}>
								<Input
									id="dealerNumber"
									name="dealerNumber"
									autoComplete="off"
									value={dealerInput}
									onChange={(e) => setDealerInput(e.target.value)}
									placeholder="e.g. C003091"
									onFocus={() => {
										if (extractCustomerNumberPart(dealerInput).length >= 3) {
											setIsSearchOpen(true);
										}
									}}
									onKeyDown={(e) => {
										if (e.key === 'Enter') {
											e.preventDefault();
											void runLookup();
										}
										if (e.key === 'Escape') {
											setIsSearchOpen(false);
										}
									}}
								/>
								{extractCustomerNumberPart(dealerInput).length >= 3 && isSearchOpen && (
									<div
										className="position-absolute w-100 border rounded shadow-sm bg-white mt-1 overflow-auto"
										style={{ zIndex: 1050, maxHeight: 280, top: '100%' }}
									>
										{isSearchLoading && (
											<div className="px-3 py-2 small text-muted">Searching…</div>
										)}
										{!isSearchLoading && searchSuggestions.length === 0 && (
											<div className="px-3 py-2 small text-muted">No matching customers</div>
										)}
										{!isSearchLoading &&
											searchSuggestions.map((c, idx) => {
												const num = c.customerNumber ?? '';
												const name = c.customerName ?? '';
												return (
													<button
														type="button"
														key={`${num}-${idx}`}
														className="dropdown-item w-100 text-start text-body text-wrap"
														onMouseDown={(e) => {
															e.preventDefault();
															setDealerInput(formatDealerInputDisplay(num, name));
															setSearchSuggestions([]);
															setIsSearchOpen(false);
															void runLookup(num);
														}}
													>
														<span className="fw-semibold">{num}</span>
														{name ? (
															<span className="text-muted"> — {name}</span>
														) : null}
													</button>
												);
											})}
									</div>
								)}
							</div>
							<Button color="primary" onClick={() => void runLookup()} disabled={isLookupLoading}>
								{isLookupLoading ? '…' : 'Lookup'}
							</Button>
						</div>
					</FormGroup>
				</Col>
			</Row>

			{customer && (
				<>
					<h4 className="mb-3">{customer.customerName ?? customer.customerNumber ?? 'Customer'}</h4>
					<Row className="g-3 mb-4">
						{(
							[
								{
									label: 'Open orders',
									value: customer.openOrdersAmount,
									onNavigate: goToOrders,
									title: 'View orders for this customer',
								},
								{
									label: 'Open invoices',
									value: customer.openInvoiceAmount,
									onNavigate: goToInvoices,
									title: 'View invoices for this customer',
								},
								{ label: 'Last year sales', value: customer.lastYearSales },
								{ label: 'YTD sales', value: customer.ytdSales },
							] satisfies ReadonlyArray<
								| { label: string; value: number | undefined; onNavigate: () => void; title: string }
								| { label: string; value: number | undefined }
							>
						).map((card) => {
							const onNavigate = 'onNavigate' in card ? card.onNavigate : undefined;
							return (
								<Col md={6} xl={3} key={card.label}>
									<Card
										className={`h-100${onNavigate ? ' shadow-sm' : ''}`}
										style={onNavigate ? { cursor: 'pointer' } : undefined}
										role={onNavigate ? 'button' : undefined}
										tabIndex={onNavigate ? 0 : undefined}
										title={'title' in card ? card.title : undefined}
										onClick={onNavigate ? () => onNavigate() : undefined}
										onKeyDown={
											onNavigate
												? (e) => {
														if (e.key === 'Enter' || e.key === ' ') {
															e.preventDefault();
															onNavigate();
														}
													}
												: undefined
										}
									>
										<CardBody>
											<p className="text-muted mb-1">{card.label}</p>
											<h5 className="mb-0">{getFormattedCurrency(card.value)}</h5>
										</CardBody>
									</Card>
								</Col>
							);
						})}
					</Row>

					<div className="mb-3">
						<Button color="secondary" onClick={() => void handlePrintPdf()} disabled={isPdfLoading}>
							{isPdfLoading ? 'Building PDF…' : 'Print tables to PDF'}
						</Button>
					</div>

					{tables.map((block) => {
						const cols = collectDisplayColumns(block.key, block.rows);
						const editable = isOnHandTable(block.key);
						const sample = block.rows[0];
						const soldKey = editable && sample
							? resolveFieldKey(sample, ['soldDate', 'SoldDate', 'sold_date'])
							: undefined;
						const rentalKey =
							editable && sample
								? resolveFieldKey(sample, ['rental', 'Rental', 'isRental'])
								: undefined;
						const serialKey =
							editable && sample
								? resolveFieldKey(sample, ['serialNumber', 'SerialNumber', 'SerNum', 'serNum'])
								: undefined;

						return (
							<div key={block.key} className="mb-4">
								<h5 className="mb-2">{humanizePropertyKey(block.key)}</h5>
								<div className="table-responsive">
									<Table bordered size="sm" className="align-middle">
										<thead className="table-light">
											<tr>
												{cols.map((c) => (
													<th key={c}>{humanizePropertyKey(c)}</th>
												))}
											</tr>
										</thead>
										<tbody>
											{block.rows.length === 0 && (
												<tr>
													<td colSpan={Math.max(cols.length, 1)} className="text-muted">
														No rows
													</td>
												</tr>
											)}
											{block.rows.map((row) => {
												const serialVal = serialKey ? String(row[serialKey] ?? '').trim() : '';
												const isSaving = savingRowIds.has(row.__rowId);
												return (
												<tr key={row.__rowId}>
													{cols.map((col) => {
														const isSoldCol =
															editable &&
															soldKey &&
															col.toLowerCase() === soldKey.toLowerCase();
														const isRentalCol =
															editable &&
															rentalKey &&
															col.toLowerCase() === rentalKey.toLowerCase();

														if (isSoldCol) {
															return (
																<td key={col}>
																	<Input
																		type="date"
																		value={toOnHandSoldDateInputValue(row[col])}
																		disabled={isSaving}
																		onClick={(e) => {
																			const input = e.currentTarget;
																			if (toOnHandSoldDateInputValue(row[col]) !== '') {
																				return;
																			}
																			const today = localTodayYmd();
																			updateOnHandRow(row.__rowId, {
																				[col]: today,
																			});
																			requestAnimationFrame(() => {
																				input.showPicker?.();
																			});
																		}}
																		onChange={(e) =>
																			updateOnHandRow(row.__rowId, {
																				[col]: e.target.value,
																			})
																		}
																		onBlur={(e) => {
																			if (!serialKey) return;
																			const r = rentalKey ? Boolean(row[rentalKey]) : false;
																			void persistOnHandUnit(
																				row.__rowId,
																				serialVal,
																				e.target.value,
																				r,
																				row,
																				{ soldKey, rentalKey },
																			);
																		}}
																	/>
																</td>
															);
														}
														if (isRentalCol) {
															const checked = Boolean(row[col]);
															return (
																<td key={col}>
																	<Input
																		type="checkbox"
																		checked={checked}
																		disabled={isSaving}
																		onChange={(e) => {
																			const nextRental = e.target.checked;
																			const prevRental = rentalKey
																				? Boolean(row[rentalKey])
																				: false;
																			updateOnHandRow(row.__rowId, {
																				[col]: nextRental,
																			});
																			if (!serialKey) return;
																			const soldRaw = soldKey ? row[soldKey] : '';
																			void (async () => {
																				const ok = await persistOnHandUnit(
																					row.__rowId,
																					serialVal,
																					soldRaw,
																					nextRental,
																					row,
																					{ soldKey, rentalKey },
																				);
																				if (!ok && rentalKey) {
																					updateOnHandRow(row.__rowId, {
																						[rentalKey]: prevRental,
																					});
																				}
																			})();
																		}}
																	/>
																</td>
															);
														}
														return (
															<td key={col}>
																{formatCellForDisplay(col, row[col], block.key)}
															</td>
														);
													})}
												</tr>
											);
											})}
										</tbody>
									</Table>
								</div>
							</div>
						);
					})}

					{tables.length === 0 && customer && (
						<p className="text-muted">No inventory tables were returned for this dealer.</p>
					)}
				</>
			)}
		</Container>
	);
};

export default DealerInventoryPage;
